<template>
  <portal to="modal" :order="order" :data-preload="[$options.name]">
    <transition
      enter-class="transitionInitial"
      enter-to-class="transitionGoal"
      enter-active-class="transitionEnterActive"
      leave-class="transitionGoal"
      leave-to-class="transitionInitial"
      leave-active-class="transitionLeaveActive"
    >
      <div v-if="isActive" :class="[classes, { 'is-black': isBlack }]">
        <DialogFrame
          v-click-outside="{
            handler: hide,
            isActive: clickOutside && isActive,
          }"
          :title="title"
          class="frame"
          :on-close-handler="hide"
          :is-black="isBlack"
        >
          <div :class="{ slotFrame: useFramePadding }">
            <slot />
          </div>

          <template v-if="buttons.length" v-slot:buttons>
            <div
              class="buttonsSection"
              :class="{
                'buttonsSection--multi': buttons.length > 1,
              }"
            >
              <Button
                v-for="(item, index) in buttons"
                :key="`button-${index}`"
                v-bind="item.config"
                :class="[
                  `button--${item.button}`,
                  `qa-DialogButton__${item.button}`,
                ]"
              />
            </div>
          </template>
        </DialogFrame>
      </div>
    </transition>
  </portal>
</template>

<script>
import DialogFrame from 'Components/01-atoms/dialog-frame/DialogFrame.vue';
import Button from 'Components/01-atoms/button/ButtonBase.vue';
import globalMixin from 'Libs/mixins/globalMixin';
import scrollMixin from 'Libs/mixins/scrollMixin';
import vClickOutside from 'v-click-outside';

export default {
  name: 'Dialog',
  directives: {
    clickOutside: vClickOutside.directive,
  },
  components: {
    Button,
    DialogFrame,
  },
  mixins: [globalMixin, scrollMixin],
  props: {
    title: {
      type: String,
      default: '',
    },
    active: {
      type: Boolean,
      default: false,
    },
    order: {
      type: Number,
      default: 0,
    },
    onClose: {
      type: [Function, Boolean],
      default: false,
    },
    confirmButton: {
      type: Object,
      default: null,
    },
    cancelButton: {
      type: Object,
      default: null,
    },
    thirdButton: {
      type: Object,
      default: null,
    },
    clickOutside: {
      type: Boolean,
      default: true,
    },
    useFramePadding: {
      type: Boolean,
      default: true,
    },
    isBlack: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isActive: this.active,
    };
  },
  computed: {
    buttons() {
      return [
        !!this.confirmButton && {
          button: 'confirm',
          config: {
            modifier: 'primary',
            ...this.confirmButton,
            onClick: ($event) => {
              if (this.confirmButton.onClick) {
                const response = this.confirmButton.onClick($event);
                if (response === 'dontClose') {
                  return;
                }
              }
              this.hide();
            },
          },
        },
        !!this.cancelButton && {
          button: 'cancel',
          config: {
            modifier: 'secondary',
            ...this.cancelButton,
            onClick: ($event) => {
              this.cancelButton.onClick && this.cancelButton.onClick($event);
              this.hide();
            },
          },
        },
        !!this.thirdButton && {
          button: 'third',
          config: {
            modifier: 'secondary',
            ...this.thirdButton,
            onClick: ($event) => {
              if (this.thirdButton.onClick) {
                const response = this.thirdButton.onClick($event);
                if (response === 'dontClose') {
                  return;
                }
              }
              this.hide();
            },
          },
        },
      ].filter((element) => !!element.config);
    },
  },
  methods: {
    show() {
      this.isActive = true;
      this.disableBodyScrolling();
    },
    hide() {
      this.isActive = false;
      this.enableBodyScrolling();
      this.$emit('hide');
    },
    onCloseHandler() {
      this.onClose && this.onClose();

      this.displayDialog = false;
    },
  },
};
</script>

<style scoped lang="scss">
@import 'variables/breakpoints';
@import 'mixins';

.dialog {
  background-color: rgba(0, 0, 0, 0.3);
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 500;
  opacity: 1;
}

.frame {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  overflow: hidden;
  width: 100%;
  max-height: calc(100vh - 40px);
  max-width: 600px;
  z-index: 40;
  scroll-padding-top: 50px;
}

.buttonsSection {
  display: flex;
  flex-flow: row nowrap;
  padding: var(--space-2);
  align-items: center;
  border-top: solid 1px var(--color-alto);
  justify-content: center;

  &--multi {
    justify-content: end;
    flex-direction: row-reverse;

    .button:last-of-type {
      margin-left: var(--space-1);
    }

    .button {
      &--cancel,
      &--confirm {
        width: 147px;
        margin-right: var(--space-1);
        text-transform: initial;
      }

      &--third {
        width: 260px;
        margin-right: var(--space-1);
        border-color: var(--color-white);
        background-color: var(--color-nero);
        color: var(--color-white);
        text-transform: initial;
      }
    }

    @media #{$_mediaMDown} {
      flex-wrap: wrap;

      .button {
        width: 100%;
        margin: 0;
        margin-bottom: var(--space-1);

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }
}

.button {
  &--confirm {
    width: 147px;
    text-transform: initial;
  }
}

.slotFrame {
  padding: var(--space-2);
  font-size: var(--font-size-S);
  line-height: 16px;
}

.transitionInitial {
  opacity: 0;
  pointer-events: none;

  .frame {
    transform: translate(-50%, calc(-50% - 50px));
    opacity: 0;
  }
}

.transitionEnterActive {
  transition: opacity var(--time-XS) ease-in;

  .frame {
    transition: opacity var(--time-XS) ease-in, transform var(--time-XS) ease-in;
  }
}

.dialog.dialog--theme-secondary {
  z-index: 1000000;
  top: -100px;
}

@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
  .transitionEnterActive {
    transition: opacity 0 ease-in;

    .frame {
      transition: opacity 0 ease-in, transform 0 ease-in;
    }
  }
}

.transitionLeaveActive {
  transition: opacity var(--time-XS) ease-in var(--time-XS);

  .frame {
    transition: opacity var(--time-XS) ease-in, transform var(--time-XS) ease-in;
  }
}

.transitionGoal {
  opacity: 1;
  pointer-events: all;

  .frame {
    transform: translate(-50%, -50%);
    opacity: 1;
  }
}

@media #{$_mediaSDown} {
  .frame {
    max-height: calc(100vh - 40px);
  }
}
</style>
